// React imports

// Third-party library imports

// Local imports � Constants
import { selectUserFilterMapping } from '../constants/common';
import {
    invoicesAmountOperatorOptions,
    invoicesCreatedInOptions,
    invoicesDueInOptions,
    invoicesAmountType,
    invoicesSortValues,
} from './invoicesSortAndFilters';
import {
    creditsCreatedSinceOptions,
    creditsAvailableSinceOptions,
    creditsAmountType,
    creditTypeStateOptions,
    creditCreatedTypeStateOptions,
    creditRecurringStateOptions,
} from './creditsSortAndFilters';
import {
    paymentResultOptions,
    paymentsAmountType,
    paymentsPaidDateOptions,
} from './paymentsSortAndFilters';
import { salesOrdersSortValues } from './salesOrdersSortAndFilters';

// Local imports � Utility functions
import { checkIfEmailIsValid } from '../utils/validationUtils';

// Local imports � Types / Interfaces
import { DynamicObject } from '../utils/commonInterfaces';

// Local config
import dayjs from 'src/config/dayjs';

export const preConfiguredFilters = {
    ALL_CUSTOMERS: 'All customers',
};

export const customersSortByOptionsMap = {
    customerCode: { label: 'Customer code', value: 'Customer code' },
    customerName: { label: 'Customer name', value: 'Company name' }
};
export const customersSortByOptions = (isDueDateView: boolean, isCalendarView: boolean) => {
    const daysView: any[] = [
        { label: '30 days', value: 'Period1' },
        { label: '60 days', value: 'Period2' },
        { label: '90+ days', value: 'Period3Plus' }
    ];
    const monthsView: any[] = [
        { label: '1 month', value: 'Period1' },
        { label: '2 months', value: 'Period2' },
        { label: '3+ months', value: 'Period3Plus' }
    ];
    const finalView = isCalendarView ? [...monthsView] : [...daysView];

    return [
        customersSortByOptionsMap.customerCode,
        customersSortByOptionsMap.customerName,
        { label: 'Total overdue', value: 'Total overdue' },
        { label: 'Total owing', value: 'Total owing' },
        isDueDateView ? { label: 'Not due', value: 'Not owing' } : undefined,
        { label: 'Current', value: 'Current' },
        ...finalView
    ].filter(o => !!o);
};

export const customersATBCalculationOptions = [
    { label: 'from create date', value: 1 },
    { label: 'from due date', value: 2 },
];

export const customersAmountATBValues = (isDueDateView: boolean, isCalendarView: boolean) => {
    const daysView: any[] = [
        { label: '30 days', value: 'Period1' },
        { label: '60 days', value: 'Period2' },
        { label: '90+ days', value: 'Period3Plus' }
    ];
    const monthsView: any[] = [
        { label: '1 month', value: 'Period1' },
        { label: '2 months', value: 'Period2' },
        { label: '3+ months', value: 'Period3Plus' }
    ];
    const finalView = isCalendarView ? [...monthsView] : [...daysView];

    return [
        { label: 'Total owing', value: 'Total owing' },
        { label: 'Total overdue', value: 'Total overdue' },
        isDueDateView ? { label: 'Not due', value: 'Not owing' } : undefined,
        { label: 'Current', value: 'Current' },
        ...finalView
    ].filter(o => !!o);
};

export const contactSelectType = [
    { label: 'That have either', value: 'true' },
    { label: 'Are missing all', value: 'false' },
];

export const customersVipStateOptions = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

export const getCustomersFilterBarFilters = (
    atbType: number,
    customerValuesOption?: any,
    customerLabel?: string,
    isCalendarView?: boolean,
    isOrgView: boolean = false,
    companyIds: string[] = [],
    conversionCurrency: number = 1
) => {
    const filters = [
        {
            filterName: 'Customer',
            filterStateName: 'Customer',
            filterElement: 'input-autocomplete',
            filterQuery: isOrgView
                ? 'GET_CUSTOMERS_FOR_ORGANISATION_AUTOCOMPLETE_FILTER'
                : 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Company name',
            filterResponse: isOrgView
                ? 'GetCustomersForOrganisation.Customers'
                : 'GetCustomersForCompany.Customers',
            filterLabelField: 'DisplayName',
            filterHiddenName: "CustomerIds",
            filterHiddenValue: "Id",
            extraQueryVariables: isOrgView
                ? {
                    CompanyIds: companyIds,
                    ConversionCurrency: conversionCurrency
                }
                : undefined
        },
        {
            filterName: 'Contact',
            filterStateName: 'Contact',
            filterElement: 'select-checkbox-group',
            filterType: 'sum',
            filterOptions: {
                select: {
                    name: 'InclusiveContact',
                    options: contactSelectType,
                },
                checkboxGroup: {
                    name: 'Contact',
                    options: [
                        { label: 'Email', value: 1 },
                        { label: 'Mobile', value: 2 },
                        { label: 'Postal address', value: 4 },
                    ],
                },
            },
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    AmountType: customersAmountATBValues(atbType === customersATBCalculationOptions[1].value, !!isCalendarView)
                },
                {
                    AmountOperator: invoicesAmountOperatorOptions,
                },
            ],
        },
        {
            filterName: 'VIP',
            filterStateName: 'IsVip',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select VIP status',
            filterOptions: customersVipStateOptions,
        },
        {
            filterName: 'VIP until',
            filterStateName: 'VipUntil',
            filterElement: 'select',
            filterType: 'text',
            filterOptions: invoicesCreatedInOptions(!!isCalendarView),
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'last-days-range',
            },
        },
        {
            filterName: 'Country',
            filterStateName: 'CustomerCountry',
            filterElement: 'input-autocomplete',
            filterQuery: isOrgView
                ? 'GET_CUSTOMERS_FOR_ORGANISATION_AUTOCOMPLETE_FILTER'
                : 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Country',
            filterResponse: isOrgView
                ? 'GetCustomersForOrganisation.Customers'
                : 'GetCustomersForCompany.Customers',
            filterLabelField: 'Country',
            extraQueryVariables: isOrgView
                ? {
                    CompanyIds: companyIds,
                    ConversionCurrency: conversionCurrency
                }
                : undefined
        }
        ,
        {
            filterName: 'Postal State',
            filterStateName: 'CustomerState',
            filterElement: 'input-autocomplete',
            filterQuery: isOrgView
                ? 'GET_CUSTOMERS_FOR_ORGANISATION_AUTOCOMPLETE_FILTER'
                : 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'State',
            filterResponse: isOrgView
                ? 'GetCustomersForOrganisation.Customers'
                : 'GetCustomersForCompany.Customers',
            filterLabelField: 'State',
            extraQueryVariables: isOrgView
                ? {
                    CompanyIds: companyIds,
                    ConversionCurrency: conversionCurrency
                }
                : undefined
        },
        {
            filterName: 'Assigned user',
            filterStateName: 'AssignedUserId',
            filterNameQuery: 'Name',
            filterElement: 'select-with-search',
            filterQuery: 'GET_USERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterResponse: 'GetUsersForCompany.CompanyUserRoles',
            filterLabelField: ['User.GivenName', 'User.FamilyName'],
            filterSort: 'Name',
            filterNameQuerySub: 'Email',
            filterSubChecking: checkIfEmailIsValid,
            filterJSONValueFieldSub: 'User.Email',
            filterHiddenName: 'UserId',
            filterHiddenValue: 'User.UserId',
            tagClass: 'wb-bw',
            filterMappingUsed: selectUserFilterMapping
            
        }
    ];

    if (customerValuesOption && customerLabel) {
        filters.splice(1, 0, {
            filterName: customerLabel,
            filterStateName: 'CustomerFirstLetter',
            filterElement: 'checkbox-group',
            filterType: 'array',
            filterOptions: customerValuesOption
        });
    }

    return filters;
};

export const customerInvoicesFilterBarFilters = (
    isCalendarView: boolean,
    isOrgView: boolean = false,
    companyId: string | undefined = undefined,
    typeLabel: string = 'Invoice'
) => {
    return [
        {
            filterName: `${typeLabel} number`,
            filterStateName: 'InvoiceNumber',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_SALES_ORDERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: salesOrdersSortValues.SALES_ORDER_NUMBER,
            filterResponse: 'GetSalesOrdersForCompany.SalesOrders',
            filterLabelField: 'Number',
            extraQueryVariables: isOrgView
                ? {
                    CompanyId: companyId
                }
                : undefined
        },
        {
            filterName: 'Created in',
            filterStateName: 'CreatedDate',
            filterElement: 'select',
            filterType: 'text',
            filterOptions: invoicesCreatedInOptions(isCalendarView),
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'last-days-range',
            },
        },
        {
            filterName: 'Due in',
            filterStateName: 'DueDate',
            filterElement: 'select',
            filterType: 'text',
            filterOptions: invoicesDueInOptions(isCalendarView),
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range',
            },
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    AmountType: invoicesAmountType,
                },
                {
                    AmountOperator: invoicesAmountOperatorOptions,
                },
            ],
        },
    ];
}

export const customerCreditsFilterBarFilters = (
    isCalendarView: boolean,
    isOrgView: boolean = false,
    companyId: string | undefined = undefined,
) => {
    return [
        {
            filterName: 'Credit number',
            filterStateName: 'CreditNumber',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CREDITS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Credit number',
            filterResponse: 'GetCreditsForCompany.Credits',
            filterLabelField: 'Number',
            extraQueryVariables: isOrgView
                ? {
                    CompanyId: companyId
                }
                : undefined
        },
        {
            filterName: 'Created since',
            filterStateName: 'CreatedDate',
            filterElement: 'select',
            filterType: 'text',
            filterMaxDate: dayjs(),
            filterOptions: creditsCreatedSinceOptions(isCalendarView),
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range-from-to-last',
            },
        },
        {
            filterName: 'Available since',
            filterStateName: 'AvailableDate',
            filterElement: 'select',
            filterType: 'text',
            filterOptions: creditsAvailableSinceOptions(isCalendarView),
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range-from-to',
            },
            // span: 5,
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    AmountType: creditsAmountType,
                },
                {
                    AmountOperator: invoicesAmountOperatorOptions,
                },
            ],
        },
        {
            filterName: 'Type',
            filterStateName: 'Type',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select type status',
            filterOptions: creditTypeStateOptions,
        },
        {
            filterName: 'Created Type',
            filterStateName: 'CreatedType',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select created type status',
            filterOptions: creditCreatedTypeStateOptions,
        },
        {
            filterName: 'Recurring',
            filterStateName: 'Recurring',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select recurring status',
            filterOptions: creditRecurringStateOptions,
        }
    ]
};

export const customerRebatesFilterBarFilters = (
    isCalendarView: boolean,
) => {
    return [
        {
            filterName: 'Rebate number',
            filterStateName: 'CreditNumber',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CREDITS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Credit number',
            filterResponse: 'GetCreditsForCompany.Credits',
            filterLabelField: 'Number',
        },
        {
            filterName: 'Created since',
            filterStateName: 'CreatedDate',
            filterElement: 'select',
            filterType: 'text',
            filterMaxDate: dayjs(),
            filterOptions: creditsCreatedSinceOptions(isCalendarView),
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range-from-to-last',
            },
            // offset: 1,
            // span: 5,
        },
        {
            filterName: 'Available since',
            filterStateName: 'AvailableDate',
            filterElement: 'select',
            filterType: 'text',
            filterOptions: creditsAvailableSinceOptions,
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range-from-to',
            },
            // span: 5,
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    AmountType: creditsAmountType,
                },
                {
                    AmountOperator: invoicesAmountOperatorOptions,
                },
            ],
        },
        {
            filterName: 'Created Type',
            filterStateName: 'CreatedType',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select created type status',
            filterOptions: creditCreatedTypeStateOptions,
        },
        {
            filterName: 'Recurring',
            filterStateName: 'Recurring',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select recurring status',
            filterOptions: creditRecurringStateOptions,
        }
    ];
}

export const getCustomerRebatesFilterBarFilters = (
    isOrgView: boolean = false,
    companyId: string | undefined = undefined
) => {
    return [
        {
            filterName: 'Rebate number',
            filterStateName: 'CreditNumber',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CREDITS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Credit number',
            filterResponse: 'GetCreditsForCompany.Credits',
            filterLabelField: 'Number',
            extraQueryVariables: isOrgView
                ? {
                    CompanyId: companyId
                }
                : undefined
        },
        {
            filterName: 'Created since',
            filterStateName: 'CreatedDate',
            filterElement: 'select',
            filterType: 'text',
            filterMaxDate: dayjs(),
            filterOptions: creditsCreatedSinceOptions,
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range-from-to-last',
            },
            // offset: 1,
            // span: 5,
        },
        {
            filterName: 'Available since',
            filterStateName: 'AvailableDate',
            filterElement: 'select',
            filterType: 'text',
            filterOptions: creditsAvailableSinceOptions,
            filterOptionsCustomTypes: {
                'Custom date range': 'date-range',
                'Custom days range': 'all-days-range-from-to',
            },
            // span: 5,
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    AmountType: creditsAmountType,
                },
                {
                    AmountOperator: invoicesAmountOperatorOptions,
                },
            ],
        },
        {
            filterName: 'Created Type',
            filterStateName: 'CreatedType',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select created type status',
            filterOptions: creditCreatedTypeStateOptions,
        },
        {
            filterName: 'Recurring',
            filterStateName: 'Recurring',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select recurring status',
            filterOptions: creditRecurringStateOptions,
        }
    ];
}

export const customerPaymentsFilterBarFilters = [
    {
        filterName: 'Result',
        filterStateName: 'Result',
        filterElement: 'checkbox-group',
        filterType: 'array',
        filterOptions: paymentResultOptions,
    },
    {
        filterName: 'Paid in',
        filterStateName: 'PaidDate',
        filterElement: 'select',
        filterType: 'text',
        filterMaxDate: dayjs(),
        filterOptions: paymentsPaidDateOptions,
        filterOptionsCustomTypes: {
            'Custom date range': 'date-range',
            'Custom days range': 'all-days-range-from-to-last',
        },
        // offset: 1,
        // span: 5,
    },
    {
        filterName: 'Amount',
        filterStateName: 'Amount',
        filterElement: 'select-multiple-and-input-amount',
        filterType: 'text',
        filterOptions: [
            {
                AmountType: paymentsAmountType,
            },
            {
                AmountOperator: invoicesAmountOperatorOptions,
            },
        ],
    },
];

export const ATBStateOptions = [
    {
        label: 'All open invoices',
        value: 'AllOpenInvoices',
    },
    {
        label: 'Overdue open invoices',
        value: 'OverdueOpenInvoices',
    },
];

export const amountTypeOptions = [
    {
        label: 'Amount Owing',
        value: 'AllOpenInvoices',
    },
    {
        label: 'Amount Overdue',
        value: 'OverdueOpenInvoices',
    },
];

export const CreditStateOptions = [
    {
        label: 'All open credits',
        value: 'AllOpenCredits',
    },
    {
        label: 'Available open credits',
        value: 'AvailableOpenCredits',
    },
];

export const getCustomersWidgetFilters = (atbType: number, isCalendarView: boolean, customerLabel: any) => {
    return [
        {
            filterName: 'Customer',
            filterStateName: 'Customer',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Company name',
            filterResponse: 'GetCustomersForCompany.Customers',
            filterLabelField: 'DisplayName',
            filterHiddenName: "CustomerIds",
            filterHiddenValue: "Id",
        },
        {
            filterName: `View associated ${customerLabel}`,
            filterStateName: 'ViewAssociatedCustomers',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select view',
            filterOptions: customersVipStateOptions,
        },
        {
            filterName: 'VIP',
            filterStateName: 'IsVip',
            filterElement: 'radio-group',
            filterType: 'text',
            filterPlaceholder: 'Select VIP Status',
            filterOptions: customersVipStateOptions,
        },
        {
            filterName: 'Country',
            filterStateName: 'CustomerCountry',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Country',
            filterResponse: 'GetCustomersForCompany.Customers',
            filterLabelField: 'Country',
        },
        {
            filterName: 'Postal State',
            filterStateName: 'CustomerState',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'State',
            filterResponse: 'GetCustomersForCompany.Customers',
            filterLabelField: 'State',
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    amountType: customersAmountATBValues(atbType === customersATBCalculationOptions[1].value, !!isCalendarView),
                    filterOptionName: 'AmountType',
                    filterPlaceholder: 'Select Amount Type',
                },
                {
                    amountOperator: invoicesAmountOperatorOptions,
                    filterOptionName: 'AmountOperator',
                    filterPlaceholder: 'Select Operator',
                },
            ],
        }
    ];
};

export const getCustomerInsightsHistoryWidgetFilters = () => {
    return [
        {
            filterName: 'Customer',
            filterStateName: 'Customer',
            filterElement: 'input-autocomplete',
            filterQuery: 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Company name',
            filterResponse: 'GetCustomersForCompany.Customers',
            filterLabelField: 'DisplayName',
            filterHiddenName: "CustomerIds",
            filterHiddenValue: "Id",
        }
    ];
};

//export const multipleCustomersFilterBarFilters = [
//    {
//        filterName: 'Customer',
//        filterStateName: 'Customer',
//        filterElement: 'input-autocomplete',
//        filterQuery: 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
//        filterSort: 'Company name',
//        filterResponse: 'GetCustomersForCompany.Customers',
//        filterLabelField: 'DisplayName',
//        extraQueryVariables: {}
//    },
//];

//export const multipleCustomersFilterBarFilters = (
//    isOrgView: boolean = false,
//    companyIds: string [] = [],
//    conversionCurrency: number = 1
//) => {
//    const filters = [...multipleCustomersFilterBarFilters];

//    if (isOrgView && filters[0]) {
//        filters[0].extraQueryVariables = {
//            CompanyId: companyId
//        };
//    }

//    return filters;
//};

export const getMultipleCustomersFilterBarFilters = (
    isOrgView: boolean = false,
    companyIds: string[] = [],
    conversionCurrency: number = 1
) => {
    return [
        {
            filterName: 'Customer',
            filterStateName: 'Customer',
            filterElement: 'input-autocomplete',
            filterQuery: isOrgView
                ? 'GET_CUSTOMERS_FOR_ORGANISATION_AUTOCOMPLETE_FILTER'
                : 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Company name',
            filterResponse: isOrgView
                ? 'GetCustomersForOrganisation.Customers'
                : 'GetCustomersForCompany.Customers',
            filterLabelField: 'DisplayName',
            filterHiddenName: "CustomerIds",
            filterHiddenValue: "Id",
            extraQueryVariables: isOrgView
                ? {
                    CompanyIds: companyIds,
                    ConversionCurrency: conversionCurrency
                }
                : undefined
        }
    ]
};

 //export const getMultipleCustomersFilterBarFilters = (
 //    isOrgView: boolean = false,
 //    companyId: string | undefined = undefined
 //) => {
 //    let adjustingMultipleCustomersFilterBarFilters: DynamicObject = multipleCustomersFilterBarFilters;
 //    if (isOrgView) {
 //        adjustingMultipleCustomersFilterBarFilters.extraQueryVariables = {
 //            CompanyId: companyId
 //        };
 //    }
 //    return {
 //        ...adjustingMultipleCustomersFilterBarFilters
 //    }
 //};

export const multipleCustomersSortByOptions = [
    { label: 'Customer code', value: 'Customer code' },
    { label: 'Company name', value: 'Company name' },

];

export const getChildCustomersFilterBarFilters = (
    atbType: number, 
    isCalendarView?: boolean,
    isOrgView: boolean = false,
    companyIds: string [] = [],
    conversionCurrency: number = 1
) => {
    const filters = [
        {
            filterName: 'Customer',
            filterStateName: 'Customer',
            filterElement: 'input-autocomplete-child-view',
            filterQuery: isOrgView
                ? 'GET_CUSTOMERS_FOR_ORGANISATION_AUTOCOMPLETE_FILTER'
                : 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
            filterSort: 'Company name',
            filterResponse: isOrgView
                ? 'GetCustomersForOrganisation.Customers'
                : 'GetCustomersForCompany.Customers',
            filterLabelField: 'DisplayName',
            filterHiddenName: "CustomerIds",
            filterHiddenValue: "Id",
            extraQueryVariables: isOrgView 
                ? {
                    CompanyIds: companyIds,
                    ConversionCurrency: conversionCurrency
                }
                : undefined,
        },
        {
            filterName: 'Amount',
            filterStateName: 'Amount',
            filterElement: 'select-multiple-and-input-amount',
            filterType: 'text',
            filterOptions: [
                {
                    AmountType: customersAmountATBValues(atbType === customersATBCalculationOptions[1].value, !!isCalendarView)
                },
                {
                    AmountOperator: invoicesAmountOperatorOptions,
                },
            ],
        },
    ];

    return filters;
};

export const customersStateFilterOptions = [
    { label: 'All Owing Customers', value: 'Owing' },
    { label: 'All Customers', value: 'All' }
];

export const multipleCustomersFilterBarFilters = (
    isOrgView: boolean = false,
    companyIds: string [] = [],
    conversionCurrency: number = 1
) => {
    return [
    {
        filterName: 'Customer',
        filterStateName: 'Customer',
        filterElement: 'input-autocomplete',
        filterQuery: isOrgView
            ? 'GET_CUSTOMERS_FOR_ORGANISATION_AUTOCOMPLETE_FILTER'
            : 'GET_CUSTOMERS_FOR_COMPANY_AUTOCOMPLETE_FILTER',
        filterSort: 'Company name',
        filterResponse: isOrgView
            ? 'GetCustomersForOrganisation.Customers'
            : 'GetCustomersForCompany.Customers',
        filterLabelField: 'DisplayName',
        extraQueryVariables: isOrgView
            ? {
                CompanyIds: companyIds,
                ConversionCurrency: conversionCurrency
            }
            : undefined
    }
    ]
};